import React, { useState, useEffect } from 'react';
import AirportSearch from './AirportSearch';
import AirlineSelect from './AirlineSelect';
import { trackAction } from '../utils/achievements';
import { API_BASE } from '../utils/api';
import { authHeaders } from '../utils/auth';

function AddRouteForm({ onRouteAdded }) {
  const [airports, setAirports] = useState([]);
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [suggestedAirlines, setSuggestedAirlines] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch airports on mount
  useEffect(() => {
    fetch(`${API_BASE}/airports`)
      .then(res => res.json())
      .then(data => setAirports(data))
      .catch(err => console.error('Error fetching airports:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!origin || !destination) {
      setError('Please select both origin and destination airports');
      return;
    }

    if (origin === destination) {
      setError('Origin and destination must be different');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/routes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({
          origin,
          destination,
          suggested_airlines: suggestedAirlines || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create route');
      }

      const newRoute = await response.json();

      if (newRoute.already_exists) {
        setSuccess(`${origin} ↔ ${destination} already exists — vote for it below!`);
      } else {
        setSuccess(`Route ${origin} → ${destination} added!`);
        trackAction('route_created');
      }

      setOrigin('');
      setDestination('');
      setSuggestedAirlines('');

      if (onRouteAdded) {
        onRouteAdded(newRoute);
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-route-section">
      <form className="add-route-form" onSubmit={handleSubmit}>
        <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Suggest New Route
        </h3>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="form-group">
          <label htmlFor="origin">Origin Airport</label>
          <AirportSearch
            id="origin"
            airports={airports}
            value={origin}
            onChange={setOrigin}
            placeholder="Search city or IATA code..."
          />
        </div>

        <div className="form-group">
          <label htmlFor="destination">Destination Airport</label>
          <AirportSearch
            id="destination"
            airports={airports}
            value={destination}
            onChange={setDestination}
            placeholder="Search city or IATA code..."
          />
        </div>

        <div className="form-group">
          <label>Suggested Airlines (Optional)</label>
          <AirlineSelect
            value={suggestedAirlines}
            onChange={setSuggestedAirlines}
            disabled={loading}
          />
        </div>

        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Suggest Route'}
        </button>
      </form>
    </div>
  );
}

export default AddRouteForm;
